import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Table,
  Space,
  PageHeader,
  Select,
  Menu,
  Dropdown,
  notification,
  Button,
  Tooltip,
  Modal,
  Input,
  DatePicker,
  Tag,
  message,
} from "antd";
import {
  MoreOutlined,
  ExclamationCircleOutlined,
  CloudDownloadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  UploadOutlined,
  DeleteOutlined,
  DownOutlined,
  FilterFilled,
  ReloadOutlined,
} from "@ant-design/icons";
import { SearchOutlined } from "@ant-design/icons";
import { CalendarOutlined } from "@ant-design/icons";
import { FaTruckPickup } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { useHistory, useLocation } from "react-router-dom";
import { reverse } from "named-urls";
import { sliceKey, reducer, actions } from "./slice";
import { orderListingSaga } from "./saga";
import moment from "moment";
import * as selectors from "./selectors";
import {
  startDateFormatter,
  endDateFormatter,
} from "../../../../utils/formatter";
import AppActions from "../../../../constants/actions";
import routes from "../../../../../routes";
import { useAuth } from "../../../../contexts/auth";
import AddInstructionsForm from "../../../../forms/AddInstructionsForm";
import AddTrackingDetailsForm from "../../../../forms/AddTrackingDetailsForm";
import Text from "antd/lib/typography/Text";
import ConfirmationModal from "./components/ConfirmationModal";
import {
  allowedStatusesForBulkLab,
  allowedStatusesForBulkLogistics,
  getBulkLabStatus,
  getBulkLogisticsStatus,
  getBulkSamplerAssignStatus,
  getBulkStatusChangeValidation,
} from "./helpers";
import AddOrderWizard from "./components/AddOrder";

const { Option } = Select;
const { confirm } = Modal;
const { RangePicker } = DatePicker;

const MENU_ACTIONS = {
  outForPickup: "OUT_FOR_PICKUP",
  receivedAtLab: "RECEIVED_AT_LAB",
  underTesting: "UNDER_TESTING",
  uploadReport: "UPLOAD_REPORT",
  completed: "COMPLETED",
  regret: "REGRET",
  details: "DETAILS",
  downloadPurchaseOrder: "DOWNLOAD_PO",
  uploadPurchaseOrder: "UPLOAD_PO",
  downloadReport: "DOWNLOAD_REPORT",
  approvePurchaseOrder: "APPROVE_PO",
  confirmPayment: "CONFIRM_PAYMENT",
  resolveDispute: "RESOLVE_DISPUTE",
  addInstructions: "ADD_INSTRUCTIONS",
  addTrackingDetails: "ADD_TRACKING_DETAILS",
  viewInstructions: "VIEW_INSTRUCTIONS",
  viewTrackingDetails: "VIEW_TRACKING_DETAILS",
  serviceRequest: "ADD_SERVICE_REQUEST",
};

const ORDER_STATUS = {
  PENDING: "pending",
  BOOKED: "booked",
  OUT_FOR_PICKUP: "outForPickup",
  RECEIVED_AT_LAB: "receivedAtLab",
  UNDER_TESTING: "underTesting",
  REPORT_UPLOADED: "reportUploaded",
  UNDER_DISPUTE: "underDispute",
  DISPUTE_RESOLVED: "disputeResolved",
  PAYMENT_VERIFIED: "paymentVerified",
  COMPLETED: "completed",
  REGRET: "regret",
};

const FileUpload = ({
  name,
  placeholder,
  onChange,
  files,
  handleRemove,
  allowZip = false,
}) => {
  const fileRef = useRef(null);
  const handleFileClick = (e) => {
    e.preventDefault();
    fileRef.current.value = "";
    fileRef.current.click();
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    onChange(files);
  };

  const getSelectedFiles = () => {
    const boxes = [];
    for (let i = 0; i < files?.length; i++) {
      let file = files.item(i);
      boxes.push(
        <div
          style={{ display: "flex", justifyContent: "space-between" }}
          key={`fileUpload_${i}`}
        >
          <p>{file.name}</p>
          <Button
            onClick={() => {
              fileRef.current.value = "";
              handleRemove();
            }}
            type="text"
            icon={<DeleteOutlined />}
            size="small"
          />
        </div>
      );
    }
    return boxes;
  };

  return (
    <div>
      <input
        ref={fileRef}
        id={name}
        style={{ display: "none" }}
        type="file"
        onChange={handleFileChange}
        accept={allowZip ? ".pdf, .zip" : ".pdf"}
      />
      <Button onClick={handleFileClick} icon={<UploadOutlined />}>
        {placeholder || "Select File"}
      </Button>
      <div style={{ padding: 10 }}>{getSelectedFiles()}</div>
    </div>
  );
};
const STORAGE_KEY = "orderListingFilters";
const getInitialState = (user, userRole, isCompanyAdmin) => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsedState = JSON.parse(saved);
      if (
        parsedState.userRole === userRole &&
        parsedState.userId === user?._id
      ) {
        let filters = parsedState.filters || {};
        if (filters.createdOn && Array.isArray(filters.createdOn)) {
          filters.createdOn = filters.createdOn.map((dateStr) =>
            moment(dateStr)
          );
        }
        return {
          currentPage: parsedState.currentPage || 1,
          pageSize: parsedState.pageSize || 10,
          prevStateFilter: parsedState.prevStateFilter || null,
          prevDistrictFilter: parsedState.prevDistrictFilter || null,
          filters: parsedState.filters || {},
        };
      }
    }
  } catch (e) {
    console.error("Failed to parse saved filters:", e);
  }

  return {
    currentPage: 1,
    pageSize: 10,
    prevStateFilter: null,
    prevDistrictFilter: null,
    filters: {},
    userRole: userRole,
    userId: user?._id,
  };
};
const OrderListing = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: orderListingSaga });
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const auth = useAuth();
  const user = auth.loggedInUser;

  const userRole = user?.role;
  const client = user?.company?._id;
  const initialState = getInitialState(user, userRole, auth.isCompanyAdmin);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardData, setWizardData] = useState({
    basicInfo: {},
    // codeListData: [],
    selectedPackage: null,
    additionalInfo: {},
  });
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(new Map());
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [poModalVisible, setPOModalVisible] = useState(false);
  const [reportOrderId, setReportOrderId] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [poOrderId, setPoOrderId] = useState(null);
  const [orderStatusForReport, setOrderStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [pageSize, setPageSize] = useState(initialState.pageSize);
  const [instrunctionModalVisible, setInstructionModalVisible] =
    useState(false);
  const [instrunctionOrderId, setInstructionOrderId] = useState(null);
  const [instrunctionId, setInstructionId] = useState(null);
  const [instructionsViewModal, setInstructionsViewModal] = useState(false);
  const [instruction, setInstructions] = useState("");
  const [purchaseOrderNum, setPurchaseOrderNum] = useState("");
  const [purchaseOrderNumberError, setPurchaseOrderNumberError] =
    useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [trackingViewModal, setTrackingViewModal] = useState(false);
  const [trackingDetails, setTrackingDetails] = useState("");
  const [reportFile, setReportFile] = useState(null);
  const [poFile, setPOFile] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [purchaseOrderFileError, setPurchaseOrderFileError] = useState(null);
  const [purchaseOrderFileSizeError, setPurchaseOrderFileSizeError] =
    useState(false);
  const [reportFileSizeError, setReportFileSizeError] = useState(false);
  const orderIdFilterRef = useRef(null);
  const dateSearchFilterRef = useRef(null);
  const [prevStateFilter, setPrevStateFilter] = useState(
    initialState.prevStateFilter
  );
  const [prevDistrictFilter, setPrevDistrictFilter] = useState(
    initialState.prevDistrictFilter
  );
  const packages = useSelector(selectors.selectPackages);
  const fetchingPackages = useSelector(selectors.selectFetchingPackages);
  const packagesError = useSelector(selectors.selectPackagesError);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isBulkLabModalVisible, setIsBulkLabModalVisible] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [isBulkLabLoading, setIsBulkLabLoading] = useState(false);
  const filters = useSelector(selectors.selectFilters);
  const [isBulkChangeStatusModalVisible, setIsBulkChangeStatusModalVisible] =
    useState(false);
  const [isBulkChangeStatusLoading, setIsBulkChangeStatusLoading] =
    useState(false);

  const [isBulkLogisticModalVisible, setIsBulkLogisticModalVisible] =
    useState(false);
  const [selectedLogistic, setSelectedLogistic] = useState(null);
  const [trackingIdInput, setTrackingIdInput] = useState("");
  const [isBulkLogisticLoading, setIsBulkLogisticLoading] = useState(false);
  const [isBulkSamplerModalVisible, setIsBulkSamplerModalVisible] =
    useState(false);
  const [selectedSampler, setSelectedSampler] = useState(null);
  const [isBulkSamplerLoading, setIsBulkSamplerLoading] = useState(false);
  const [isTrackingModalVisible, setIsTrackingModalVisible] = useState(false);
  const [isTrackingLoading, setIsTrackingLoading] = useState(false);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState(false);
  const [selectedLogisticPartner, setSelectedLogisticPartner] = useState(null);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [selectedLabChange, setSelectedLabChange] = useState(null);
  const [confirmationType, setConfirmationType] = useState(null);
  const [selectedSamplerChange, setSelectedSamplerChange] = useState(null);
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [lastRefreshTime, setLastRefreshTime] = useState(Date.now());

  const handleStepChange = useCallback(
    (newStep, data = {}) => {
      setWizardData((prev) => {
        const updates = { ...prev };

        switch (wizardStep) {
          case 0:
            updates.basicInfo = { ...prev.basicInfo, ...data };
            break;
          // case 1:
          // updates.codeListData = data;
          // break;
          case 1:
            updates.selectedPackage = { ...prev.selectedPackage, ...data };
            break;
          case 2:
            updates.additionalInfo = { ...prev.additionalInfo, ...data };
            break;
          default:
        }

        return updates;
      });
      console.log({ newStep });
      setWizardStep(newStep);
    },
    [wizardStep]
  );
  // Simple navigation helpers
  const goNext = useCallback(
    (data) => {
      handleStepChange(wizardStep + 1, data);
    },
    [wizardStep, handleStepChange]
  );

  const goBack = useCallback(
    (data) => {
      handleStepChange(wizardStep - 1, data);
    },
    [wizardStep, handleStepChange]
  );

  // const {
  //   control: addOrderControl,
  //   handleSubmit: addOrderHandleSubmit,
  //   formState: { errors: addOrderErrors },
  //   reset: resetAddOrderForm,
  //   watch: addOrderWatch,
  //   setValue: addOrderSetValue,
  // } = useForm({
  //   resolver: yupResolver(addOrderSchema),
  //   defaultValue: {
  //     state: "",
  //     district: "",
  //     commodity: "",
  //     sampleVariant: "",
  //     sampleType: "",
  //     sector: "",
  //     sampler: "",
  //     projectCode: "",
  //     client: "",
  //   },
  // });

  // const selectedState = addOrderWatch("state");
  // const selectedDistrict = addOrderWatch("district");

  // useEffect(() => {
  //   if (selectedState) {
  //     dispatch(actions.fetchDistricts({ stateId: selectedState }));
  //     addOrderSetValue("district", "");
  //   }
  // }, [dispatch, selectedState, addOrderSetValue]);

  // useEffect(() => {
  //   if (selectedDistrict && selectedState) {
  //     dispatch(
  //       actions.fetchSamplers({
  //         districtId: selectedDistrict,
  //         stateId: selectedState,
  //       })
  //     );
  //     addOrderSetValue("sampler", "");
  //   }
  // }, [dispatch, selectedDistrict, selectedState, addOrderSetValue]);

  // const onAddOrderSubmit = (data) => {
  //   dispatch(
  //     actions.addOrder({
  //       data,
  //       onSuccess: (response) => {
  //         notification.success({
  //           message: "Order added successfully!",
  //           description: `Order ID: ${response.document.orderId}`,
  //           placement: "bottomLeft",
  //         });
  //         resetAddOrderForm();
  //       },
  //       onFailure: (error) => {
  //         notification.error({
  //           message: error?.message || "Failure in adding order",
  //           placement: "bottomLeft",
  //         });
  //       },
  //     })
  //   );
  //   setAddOrderModalVisible(false);
  // };
  useEffect(() => {
    if (userRole === "company_admin" && open && client) {
      setWizardData((prev) => ({
        ...prev,
        basicInfo: {
          ...prev.basicInfo,
          client: client,
        },
      }));
    }
  }, [open, client, userRole]);

  useEffect(() => {
    if (open && wizardData.basicInfo.state) {
      dispatch(actions.fetchDistricts({ stateId: wizardData.basicInfo.state }));
    }
  }, [open, wizardData.basicInfo.state, dispatch]);

  useEffect(() => {
    if (open && wizardData.basicInfo.district && wizardData.basicInfo.state && wizardData.basicInfo.agency) {
      dispatch(
        actions.fetchSamplers({
          districtId: wizardData.basicInfo.district,
          stateId: wizardData.basicInfo.state,
          agencyId: wizardData.basicInfo.agency,
        })
      );
    }
  }, [
    dispatch,
    open,
    wizardData.basicInfo.district,
    wizardData.basicInfo.state,
    wizardData.basicInfo.agency,
  ]);

  // Fetch project codes when client changes
  useEffect(() => {
    if (open && wizardData.basicInfo.client) {
      dispatch(
        actions.fetchProjectCodes({
          companyId: wizardData.basicInfo.client,
          for: "AddOrder",
        })
      );
    }
  }, [open, wizardData.basicInfo.client, dispatch]);

  useEffect(() => {
    if (
      open &&
      wizardData.basicInfo.client &&
      wizardData.basicInfo.commodity &&
      wizardData.basicInfo.variant
    ) {
      dispatch(
        actions.fetchPackages({
          companyId: wizardData.basicInfo.client,
          commodityId: wizardData.basicInfo.commodity,
          sampleVariant: wizardData.basicInfo.variant,
        })
      );
    } else if (open) {
      dispatch(actions.clearPackages());
    }
  }, [
    open,
    wizardData.basicInfo.client,
    wizardData.basicInfo.commodity,
    wizardData.basicInfo.variant,
    dispatch,
  ]);

  useEffect(() => {
    const initialState = getInitialState(user, userRole, auth.isCompanyAdmin);
    if (initialState.filters && Object.keys(initialState.filters).length > 0) {
      dispatch(actions.setFilters(initialState.filters));
    }
  }, []);

  useEffect(() => {
    const state = {
      currentPage,
      pageSize,
      prevStateFilter,
      prevDistrictFilter,
      filters,
      userRole: userRole,
      userId: user?._id,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error("Failed to save filters:", e);
    }
  }, [
    currentPage,
    pageSize,
    prevStateFilter,
    prevDistrictFilter,
    filters,
    userRole,
    user,
  ]);

  // Fetch dropdown data (only once on mount)
  useEffect(() => {
    dispatch(actions.fetchStates());
    dispatch(actions.fetchCommodities());
    dispatch(actions.fetchDropdown(["orderStatus"]));
    dispatch(actions.fetchSectors());
    dispatch(actions.fetchAgencies());

    if (auth.isAuthorised(AppActions.BO_UPDATE_ORDER_LOGISTICS)) {
      dispatch(actions.fetchLogisticPartners());
    }
    if (auth.isAuthorised(AppActions.BO_UPDATE_ORDER_LAB)) {
      dispatch(actions.fetchLabs());
    }
    if (
      !auth.isCompanyAdmin &&
      userRole !== "regulatory_body_admin" &&
      userRole !== "csc_admin"
    ) {
      dispatch(actions.fetchCompanies());
    }
    if (auth.isCompanyAdmin) {
      dispatch(actions.fetchCompanyUsers({ id: user.company._id }));
    }
    if (
      userRole === "company_admin" ||
      userRole === "regulatory_body_admin" ||
      userRole === "csc_admin"
    ) {
      dispatch(actions.fetchProjectCodes({ for: "filter" }));
    }
  }, [dispatch, auth, user, userRole]);

  const orders = useSelector(selectors.selectOrders) || [];
  const dropdowns = useSelector(selectors.selectDropdowns);
  const labs = useSelector(selectors.selectLabs) || [];
  const logisticPartners = useSelector(selectors.selectLogisticPartners);
  const { orderStatus = [] } = dropdowns;
  const labsUpdating = useSelector(selectors.selectUpdatingLab);
  const samplerUpdating = useSelector(selectors.selectUpdatingSampler);
  const logisticsUpdating = useSelector(selectors.selectUpdatingLogistics);
  const orderPageLoading = useSelector(selectors.selectOrderPageLoading);
  const loading = useSelector(selectors.selectLoading);
  const totalOrders = useSelector(selectors.selectOrderCount);
  const companies = useSelector(selectors.selectCompanies);
  const error = useSelector(selectors.selectError);
  const users = useSelector(selectors.selectUsers) || [];

  const commodities = useSelector(selectors.selectCommodities) || [];
  const states = useSelector(selectors.selectStates) || [];
  const districts = useSelector(selectors.selectDistricts) || [];
  const samplers = useSelector(selectors.selectSamplers) || [];
  const projectCodesForAddOrder = useSelector(
    selectors.selectProjectCodesForAddOrder
  );
  const projectCodesForFilter = useSelector(
    selectors.selectProjectCodesForFilter
  );
  const sectors = useSelector(selectors.selectSectors);
  const {
    variant = [
      { name: "Loose", _id: "Loose" },
      { name: "Normal", _id: "Normal" },
      { name: "Organic", _id: "Organic" },
    ],
  } = dropdowns;

  // Helper function to build query params
  const buildQueryParams = useCallback((filterData, page, limit) => {
    const queryParams = new URLSearchParams();
    queryParams.append("page", page);
    queryParams.append("limit", limit);

    if (filterData.status) {
      filterData.status.forEach((cat) => queryParams.append("statusId", cat));
    }
    if (filterData.lab) {
      filterData.lab.forEach((el) => queryParams.append("labAssignedId", el));
    }
    if (filterData.logistics) {
      filterData.logistics.forEach((el) =>
        queryParams.append("assignedLogisticsId", el)
      );
    }
    if (filterData.userName) {
      filterData.userName.forEach((el) => queryParams.append("user", el));
    }
    if (filterData.orderId) {
      queryParams.append("orderId", filterData.orderId);
    }
    if (filterData.sampleSeal) {
      queryParams.append("sampleSealNo", filterData.sampleSeal);
    }
    if (filterData.commodity) {
      filterData.commodity.forEach((el) =>
        queryParams.append("commodityId", el)
      );
    }
    if (filterData.variant) {
      filterData.variant.forEach((el) =>
        queryParams.append("sampleVariant", el)
      );
    }
    if (filterData.state) {
      filterData.state.forEach((el) => queryParams.append("stateId", el));
    }
    if (filterData.district) {
      filterData.district.forEach((el) => queryParams.append("districtId", el));
    }
    if (filterData.sampler && filterData.sampler.length > 0) {
      filterData.sampler.forEach((el) => queryParams.append("samplerId", el));
    }
    if (filterData.createdOn?.length === 2) {
      const startDate = startDateFormatter(filterData.createdOn[0]);
      const endDate = endDateFormatter(filterData.createdOn[1]);
      queryParams.append("createdAtFrom", startDate);
      queryParams.append("createdAtTo", endDate);
    }
    if (filterData.company) {
      queryParams.append("companyId", filterData.company);
    }
    if (filterData.projectCodeId) {
      queryParams.append("projectCodeId", filterData.projectCodeId);
    }
    if (filterData.sector) {
      filterData.sector.forEach((el) => queryParams.append("sector", el));
    }

    return queryParams;
  }, []);

  // Build order list query from current filters
  const buildOrderListQuery = useCallback(() => {
    const queryParams = buildQueryParams(filters, currentPage, pageSize);
    return queryParams.toString();
  }, [filters, currentPage, pageSize, buildQueryParams]);

  // Fetch orders and dependent data when filters/pagination change
  useEffect(() => {
    // Fetch dependent dropdowns based on filters
    if (filters?.state) {
      dispatch(actions.fetchDistricts({ stateId: filters.state }));
    }
    if (filters?.district && filters?.state) {
      dispatch(
        actions.fetchSamplers({
          stateId: filters.state,
          districtId: filters.district,
        })
      );
    }
    if (filters?.company) {
      dispatch(
        actions.fetchProjectCodes({ companyId: filters.company, for: "filter" })
      );
    }

    // Fetch orders with current filters
    const query = buildOrderListQuery();
    dispatch(actions.fetchOrders({ query }));
    dispatch(actions.fetchPendingOrderReport());
  }, [filters, currentPage, pageSize, dispatch, buildOrderListQuery]);

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error Message",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeError());
      }, 1500);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
  }, [orders]);

  const shouldShowPaymentWarning = (id, paymentComplete, status) => {
    if (
      !paymentComplete &&
      (status === "Report Uploaded" ||
        status === "Dispute Resolved" ||
        status === "Under Dispute")
    ) {
      return true;
    } else return false;
  };

  const orderData = orders.map((order) => {
    let obj = {
      userName: `${order?.user?.firstName} ${order?.user?.lastName}`,
      userCompany: `${order?.company?.companyName}`,
      companyId: order?.company?._id,
      id: order._id,
      orderId: order.orderId,
      lab: order?.assignedLab,
      logistics: order?.assignedLogistics?._id,
      status: order?.orderStatus?.label || "NA",
      createdOn: moment.utc(order.createdAt).format("DD MMM YYYY, hh:mm A"),
      purchaseOrderFile: order.purchaseOrderFile,
      orderStatus: order.status,
      reportFile: order?.labResponseId?.reportFile,
      instruction: order.instruction,
      trackingDetails: order.trackingDetails,
      showPaymentWarning: shouldShowPaymentWarning(
        order?.company?._id,
        order?.paymentComplete,
        order?.orderStatus?.label
      ),
      paymentComplete: order.paymentComplete,
      state: order.state,
      district: order.district,
      samplerInfo: order?.samplerInfo || null,
      commodity: order.commodity,
      variant: order.sampleVariant,
      sampleSeal: order?.sampleSealNo || "NA",
      sector: order?.sector,
      trackingId: order?.trackingId,
      orderData: order,
    };
    return obj;
  });

  const getLabOptions = () => {
    const boxes = [];
    if (labs && labs.length) {
      labs.forEach((lab) => {
        boxes.push(
          <Option key={lab._id} value={lab._id}>
            {lab.name}
          </Option>
        );
      });
    }

    return boxes;
  };

  const getLogisticsOptions = () => {
    const boxes = [];
    if (logisticPartners && logisticPartners.length) {
      logisticPartners.forEach((lab) => {
        boxes.push(
          <Option key={lab._id} value={lab._id}>
            {lab.name}
          </Option>
        );
      });
    }
    return boxes;
  };
  const handleFieldChange = (changedValues) => {
    setWizardData((prev) => ({
      ...prev,
      basicInfo: { ...prev.basicInfo, ...changedValues },
    }));
  };
  const fetchOderList = useCallback(() => {
    const query = buildOrderListQuery();
    dispatch(actions.fetchOrders({ query }));
  }, [buildOrderListQuery, dispatch]);

  const handleChangeLab = ({ id, value }) => {
    dispatch(
      actions.updateLab({
        id,
        data: [{ orderId: id, labToBeAssignedId: value }],
        onSuccess: () => {
          notification.success({
            message: "Lab got assigned!",
          });
          setIsConfirmationModalVisible(false);
          setIsConfirmLoading(false);
          setConfirmationType(null);
          fetchOderList();
        },
        onFailure: () => {
          notification.error({
            message: "Failure in assigneing Lab",
          });
          setIsConfirmLoading(false);
        },
      })
    );
  };

  const handleChangeSampler = ({ id, value }) => {
    dispatch(
      actions.updateSampler({
        id,
        data: [{ orderId: id, samplerId: value }],
        onSuccess: () => {
          notification.success({
            message: "Sampler got assigned!",
          });
          setIsConfirmationModalVisible(false);
          setIsConfirmLoading(false);
          setConfirmationType(null);
          fetchOderList();
        },
        onFailure: () => {
          notification.error({
            message: "Failure in assigning Sampler",
          });
          setIsConfirmLoading(false);
        },
      })
    );
  };

  const handleChangeLogisticPartner = ({ id, value }) => {
    const data = [{ orderId: id, logisticId: value }];
    dispatch(
      actions.logisticsAssignment({
        data,
        onSuccess: () => {
          notification.success({
            message: "Logistic Partner successfully assigned!",
          });
          setIsConfirmationModalVisible(false);
          setIsConfirmLoading(false);
          setConfirmationType(null);
          fetchOderList();
        },
        onFailure: () => {
          notification.error({
            message: "Failure in assigning Logistic Partner",
          });
          setIsConfirmLoading(false);
        },
      })
    );
  };

  const getOrderStatusId = (value) => {
    const status = orderStatus.find((o) => o.value === value);
    if (status) return status._id;
    return null;
  };

  const showOrderUpdateConfirmation = ({
    id,
    orderId,
    newStatus,
    newStatusText,
    currentStatusText,
  }) => {
    confirm({
      title: "Confirm Status Update",
      icon: <ExclamationCircleOutlined />,
      content: `Order status will be updated from '${currentStatusText}' to '${newStatusText}'`,
      onOk() {
        dispatch(
          actions.updateOrderStatus({
            id,
            data: { status: getOrderStatusId(newStatus) },
            onSuccess: () =>
              notification.success({
                message: "Status Update Success!",
                description: `Status of order id: ${orderId} is updated to '${newStatusText}'`,
              }),
            onFailure: () =>
              notification.error({
                message: "Error Updating Status!",
                description: `An error occured while updating status of order id: ${orderId} to '${newStatusText}'. Please try again!`,
              }),
          })
        );
      },
      onCancel() {
        // console.log("Cancel");
      },
    });
  };

  const onClick = ({ key, id, data }) => {
    switch (key) {
      case MENU_ACTIONS.downloadReport:
        dispatch(
          actions.downloadPurchaseOrder({
            id,
            data: { fileName: data.reportFile },
          })
        );
        break;
      case MENU_ACTIONS.completed:
        showOrderUpdateConfirmation({
          id,
          orderId: data.orderId,
          newStatus: ORDER_STATUS.COMPLETED,
          newStatusText: "Completed",
          currentStatusText: "Current Status",
        });
        break;
      case MENU_ACTIONS.regret:
        showOrderUpdateConfirmation({
          id,
          orderId: data.orderId,
          newStatus: ORDER_STATUS.REGRET,
          newStatusText: "Regret",
          currentStatusText: "Current Status",
        });
        break;
      case MENU_ACTIONS.details:
        history.push(
          reverse(`${routes.backOffice.orders.details.view}`, { orderId: id })
        );
        break;
      case "addTrackingDetails":
        setIsTrackingModalVisible(true);
        setOrderDetails(data.orderData);
        break;
      default:
        break;
    }
  };

  const getActions = (order) => {
    const currentStatus = order?.orderStatus?.value;

    const completeOrRegret =
      currentStatus === ORDER_STATUS.COMPLETED ||
      currentStatus === ORDER_STATUS.REGRET;

    const allowedActions = [];

    if (auth.isAuthorised(AppActions.BO_ACCESS_ORDER_DETAILS)) {
      allowedActions.push({
        key: MENU_ACTIONS.details,
        icon: <InfoCircleOutlined />,
        label: "Details",
        disabled: false,
      });
    }

    if (
      (currentStatus === ORDER_STATUS.UNDER_TESTING ||
        currentStatus === ORDER_STATUS.REPORT_UPLOADED ||
        currentStatus === ORDER_STATUS.UNDER_DISPUTE ||
        currentStatus === ORDER_STATUS.DISPUTE_RESOLVED ||
        currentStatus === ORDER_STATUS.PAYMENT_VERIFIED) &&
      auth.isAuthorised(AppActions.BO_UPLOAD_REPORT)
    ) {
    }
    if (order.reportFile && auth.isAuthorised(AppActions.BO_DOWNLOAD_REPORT)) {
      allowedActions.push({
        key: MENU_ACTIONS.downloadReport,
        icon: <CloudDownloadOutlined />,
        label: "Download Report",
        disabled: false,
      });
    }

    if (
      order.paymentComplete &&
      auth.isAuthorised(AppActions.BO_UPDATE_ORDER_STATUS) &&
      order.status === "Report Uploaded"
    ) {
      allowedActions.push({
        key: MENU_ACTIONS.completed,
        icon: <CheckCircleOutlined />,
        label: "Mark as Complete",
        disabled: false,
      });
    }

    if (userRole !== "support_team" && userRole !== "csc_admin") {
      allowedActions.push({
        key: "addTrackingDetails",
        icon: <FaTruckPickup />,
        label: order?.trackingId
          ? "Edit Tracking Details"
          : "Add Tracking Details",
      });
    }

    return allowedActions;
  };

  const getMenuItems = (order) => {
    const allowedActions = getActions(order);
    return (
      <Menu onClick={({ key }) => onClick({ id: order.id, key, data: order })}>
        {allowedActions.map(({ key, icon, disabled, label }) => (
          <Menu.Item key={key} icon={icon} disabled={disabled}>
            {label}
          </Menu.Item>
        ))}
      </Menu>
    );
  };

  const handleReportUploadSuccess = () => {
    setReportOrderId(null);
    setOrderStatus(null);
    setReportModalVisible(false);
    setReportFile(null);
    setOrderId(null);
    setCompanyId(null);
    notification.success({
      message: "Report uploaded successfully!!",
    });
  };

  const handleReportUploadFailure = () => {
    setReportOrderId(null);
    setOrderStatus(null);
    setOrderId(null);
    setCompanyId(null);
    setReportModalVisible(false);
    setReportFile(null);
    notification.error({
      message: "There was some error while uploading Report",
    });
  };

  const handleUploadReport = () => {
    if (reportFile?.length) {
      const payloadData = {
        file: reportFile[0],
        id: reportOrderId,
        source: "Order Details",
        fileUsedFor: "Lab Reports",
        companyId: companyId,
        orderId: orderId,
        onSuccess: handleReportUploadSuccess,
        onFailure: handleReportUploadFailure,
      };
      if (orderStatusForReport === ORDER_STATUS.UNDER_TESTING) {
        payloadData.status = getOrderStatusId(ORDER_STATUS.REPORT_UPLOADED);
      }
      dispatch(actions.uploadReport(payloadData));
    } else {
      setReportFile(null);
    }
  };

  const handlePOUploadSuccess = () => {
    setPoOrderId(null);
    setPOFile(null);
    setPurchaseOrderNum("");
    setPOModalVisible(false);
    setPOFile(null);
    notification.success({
      message: " Uploaded PO successfully",
    });
  };

  const handlePOUploadFailure = () => {
    setReportOrderId(null);
    setPOModalVisible(false);
    setPOFile(null);
    notification.error({
      message: "Error Uploading PO!",
      description: `An error occured while uploading PO. Please ensure PO number is unique and try again.`,
    });
  };

  const handleUploadPurchaseOrder = () => {
    if (poFile?.length && purchaseOrderNum) {
      if (purchaseOrderNum.match(/^(?!\s+$).+/) === null)
        setPurchaseOrderNumberError("Please provide a valid PO Details");
      else {
        const payloadData = {
          file: poFile[0],
          id: poOrderId,
          purchaseOrderNumber: purchaseOrderNum,
          source: "Purchase Order",
          companyId: companyId,
          orderId: orderId,
          onSuccess: handlePOUploadSuccess,
          onFailure: handlePOUploadFailure,
        };
        console.log(payloadData);
        dispatch(actions.uploadPurchaseOrder(payloadData));
      }
    } else {
      setPOFile(null);
      setPurchaseOrderNum("");
    }
  };

  const handleOrderInstructions = (data) => {
    dispatch(
      actions.updateOrder({
        id: instrunctionId,
        data,
        onSuccess: () => {
          setInstructionId(null);
          setInstructionOrderId(null);
          setInstructionModalVisible(false);
          notification.success({
            message: "Success!",
            description: `Instructions added to order`,
          });
        },
        onFailure: () => {
          setInstructionId(null);
          setInstructionOrderId(null);
          setInstructionModalVisible(false);
          notification.error({
            message: "Error Updating Instructions!",
            description: `An error occured while updating instructions of order id: ${instrunctionOrderId}. Please try again!`,
          });
        },
      })
    );
  };

  const handleCancelReportUpload = () => {
    setReportModalVisible(false);
    setReportFile(null);
    setReportError(null);
    setReportFileSizeError(false);
  };

  const handleCancelPurchaseOrderUpload = () => {
    setPurchaseOrderNum("");
    setPurchaseOrderFileError(null);
    setPurchaseOrderFileSizeError(false);
    setPOFile("");
    setPurchaseOrderNumberError(null);
    setPOModalVisible(false);
  };

  const handleFileChange = (files) => {
    const acceptedFilesExtension = [
      "zip",
      "application/pdf",
      "application/octet-stream",
      "application/zip",
      "application/x-zip",
      "application/x-zip-compressed",
    ];
    if (acceptedFilesExtension.indexOf(files[0]?.type) === -1) {
      setReportError(true);
      setReportFile(null);
      return;
    } else if (files[0]?.size > 12 * 1024 * 1024) {
      setReportFileSizeError(true);
    } else {
      setReportError(null);
      setReportFile(files);
      setReportFileSizeError(false);
    }
  };

  const handleRemoveFile = () => {
    setReportFile(null);
  };

  const handlePOFileChange = (files) => {
    if (files[0]?.type !== "application/pdf") {
      setPurchaseOrderFileError(true);
      setPOFile(null);
      return;
    } else if (files[0]?.size > 12 * 1024 * 1024) {
      setPurchaseOrderFileSizeError(true);
    }
    setPurchaseOrderFileError(null);
    setPOFile(files);
  };

  const handleRemovePOFile = () => {
    setPOFile(null);
  };
  const enableLogisticsChange = (order) => {
    const currentStatus = order?.orderStatus?.value;
    let checkForCondition = [ORDER_STATUS.BOOKED].indexOf(currentStatus);

    if (checkForCondition === -1) return true;
    else {
      if (!order.lab) return true;
      else return false;
    }
  };

  const enableLabChange = (order) => {
    const currentStatus = order?.orderStatus?.value;
    return [ORDER_STATUS.BOOKED].indexOf(currentStatus) !== -1;
  };

  const getStatusFilters = () => {
    if (!orderStatus?.length) return [];
    let filter = orderStatus.filter((status) => {
      if (["Dispute Resolved"].indexOf(status.label) === -1) return status;
    });
    filter = filter
      .map((status) => ({
        text: status.label,
        value: status._id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
    return filter;
  };

  const getVariantFilters = () => {
    const filter = variant
      .map((entity) => ({
        text: entity.name,
        value: entity._id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
    return filter;
  };

  const getCommodityFilters = () => {
    return commodities
      .map((entity) => ({
        text: entity.name,
        value: entity._id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  };

  const getStateFilters = () => {
    return states
      .map((entity) => ({
        text: entity.name,
        value: entity._id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  };

  const getDistrictFilters = () => {
    return districts
      .map((entity) => ({
        text: entity.city,
        value: entity._id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  };

  const getSamplerFilters = () => {
    if (!samplers?.length) return [];
    return samplers
      .map((entity) => ({
        text: entity.name,
        value: entity._id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  };

  const getLabFilters = () => {
    if (!labs?.length) return [];
    return labs
      .map((entity) => ({
        text: entity.name,
        value: entity._id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  };

  const getLogisticsFilters = () => {
    if (!logisticPartners?.length) return [];
    return logisticPartners
      .map((entity) => ({
        text: entity.name,
        value: entity._id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  };

  const getCompanyFilters = () => {
    if (!companies?.length) return [];
    return companies
      .map((entity) => ({
        text: entity.companyName,
        value: entity._id,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  };

  const getUsersFilters = () => {
    if (!users.length) return [];
    const filter = users
      .map((user) => ({
        text: user.label,
        value: user.value,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
    return filter;
  };

  const getSectorsFilters = () => {
    if (!sectors.length) return [];
    const filter = sectors
      .map((sector) => ({
        text: sector.label,
        value: sector.value,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
    return filter;
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      orderIdFilterRef.current = { confirm, clearFilters };
      return (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={`Search...`}
            value={selectedKeys}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? e.target.value : "")
            }
            onPressEnter={() => {
              confirm({ closeDropdown: true });
            }}
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm({ closeDropdown: true });
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm({ closeDropdown: true });
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#0a8080" : undefined }} />
    ),
  });

  const getDateSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => {
      dateSearchFilterRef.current = { confirm, clearFilters };
      return (
        <div style={{ padding: 8 }}>
          <div style={{ marginBottom: 10 }}>
            <RangePicker
              value={selectedKeys}
              onChange={(e) => setSelectedKeys(e ? e : [])}
              onPressEnter={() => {
                confirm({ closeDropdown: true });
              }}
            />
          </div>
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm({ closeDropdown: true });
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm({ closeDropdown: true });
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered) => (
      <CalendarOutlined style={{ color: filtered ? "#0a8080" : undefined }} />
    ),
  });

  const statusColors = {
    Pending: "#FFA500",
    "Report Uploaded": "#4CAF50",
    "Out for Pickup": "#2196F3",
    "Under Testing": "#9C27B0",
    Regret: "#F44336",
    "Rejected By Lab": "#FF5252",
    Booked: "#00BCD4",
    "Received at Lab": "#3F51B5",
    Completed: "#4CAF50",
    "Not Found": "#795548",
    "Need Correction": "#FF9800",
    "In Review": "#607D8B",
    "Ready for Pickup": "#8BC34A",
    "Deliverd to Lab": "#3F51B5",
    "Order Correction": "#ff8000ff",
  };

  const allColumns = [
    {
      title: "Order ID",
      dataIndex: "orderId",
      width: "17%",
      fixed: "left",
      ...getColumnSearchProps("orderId"),
      filteredValue: filters.orderId || null,
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text}
        </div>
      ),
    },
    // {
    //   title: "User",
    //   dataIndex: "userName",
    //   width: "8%",
    //   filters: auth.isCompanyAdmin ? getUsersFilters() : "",
    //   render: (text) => (
    //     <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
    //       {text}
    //     </div>
    //   ),
    //   hidden: userRole === "company_admin",
    // },
    // {
    //   title: "Company",
    //   dataIndex: "userCompany",
    //   width: "8%",
    //   filters: !auth.isCompanyAdmin ? getCompanyFilters() : "",
    //   filteredValue: filters.userCompany || null,
    //   filterMode: "menu",
    //   filterSearch: true,
    //   render: (text) => (
    //     <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
    //       {text}
    //     </div>
    //   ),
    //   hidden: userRole === "company_admin",
    // },
    {
      title: "Logistics",
      dataIndex: "logistics",
      width: "12%",
      filters: getLogisticsFilters(),
      filteredValue: filters.logistics || null,
      filterMode: "menu",
      filterSearch: true,
      hidden: !auth.isAuthorised(AppActions.BO_UPDATE_ORDER_LOGISTICS),
      render: (text, record) => {
        if (userRole === "support_team" && userRole === "csc_admin") {
          if (!text) {
            return (
              <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                -
              </div>
            );
          }
          const selectedLogistics = logisticPartners?.find(
            (logistics) => logistics._id === text
          );
          const displayText = selectedLogistics?.name || text;

          return (
            <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
              {displayText}
            </div>
          );
        }
        const isLogisticEnable = allowedStatusesForBulkLogistics.has(
          record.orderData?.orderStatus?.value
        );
        return (
          <Tooltip
            title={
              isLogisticEnable
                ? ""
                : "Logistic change only allowed for Booked, in Review, Ready For Pickup or Out For Pickup"
            }
          >
            <Select
              showSearch
              loading={!!logisticsUpdating[record.id]}
              disabled={!!logisticsUpdating[record.id] || !isLogisticEnable}
              style={{ width: "100%" }}
              placeholder="Select Logistic Partner"
              optionFilterProp="children"
              value={text || ""}
              onChange={(val) => {
                setSelectedLogisticPartner({ id: record.orderId, value: val });
                setConfirmationType("logistics");
                setIsConfirmationModalVisible(true);
              }}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getLogisticsOptions()}
            </Select>
          </Tooltip>
        );
      },
    },
    {
      title: "Lab",
      dataIndex: "lab",
      width: "12%",
      filters: getLabFilters(),
      filteredValue: filters.lab || null,
      filterMode: "menu",
      filterSearch: true,
      hidden: !auth.isAuthorised(AppActions.BO_UPDATE_ORDER_LAB),
      render: (text, record) => {
        if (userRole === "support_team" && userRole === "csc_admin") {
          if (!text) {
            return (
              <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                -
              </div>
            );
          }
          const selectedLab = labs?.find((lab) => lab._id === text);
          const displayText = selectedLab?.name || text;

          return (
            <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
              {displayText}
            </div>
          );
        }

        const isLabEnable = allowedStatusesForBulkLab.has(
          record.orderData?.orderStatus?.value
        );
        return (
          <Tooltip
            title={
              isLabEnable
                ? ""
                : "Lab change only allowed for Booked, in Review, Ready For Pickup, Out For Pickup, Delivered to Lab, needCorrection."
            }
          >
            <Select
              showSearch
              loading={!!labsUpdating[record.id]}
              disabled={!isLabEnable}
              style={{ width: "100%" }}
              placeholder="Select lab"
              optionFilterProp="children"
              value={text || ""}
              onChange={(val) => {
                setSelectedLabChange({ id: record.orderId, value: val });
                setConfirmationType("lab");
                setIsConfirmationModalVisible(true);
              }}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getLabOptions()}
            </Select>
          </Tooltip>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "createdOn",
      width: "13%",
      ...getDateSearchProps("createdOn"),
      filteredValue: filters.createdOn || null,
      render: (text) => <div style={{ whiteSpace: "nowrap" }}>{text}</div>,
    },
    {
      title: "State",
      dataIndex: "state",
      width: "10%",
      filteredValue: filters.state || null,
      filters: getStateFilters(),
      filterMode: "menu",
      filterSearch: true,
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text}
        </div>
      ),
    },
    {
      title: "District",
      dataIndex: "district",
      width: "10%",
      filteredValue: filters.district || null,
      filters:
        filters.state && filters.state.length > 0 ? getDistrictFilters() : [],
      filterMode: "menu",
      filterSearch: true,
      ...(filters.state && filters.state.length > 0
        ? {}
        : {
          filterDropdown: () => {
            return (
              <div style={{ padding: 8 }}>Please select state first</div>
            );
          },
        }),
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text}
        </div>
      ),
    },
    {
      title: "Sampler",
      dataIndex: "sampler",
      width: "12%",
      filteredValue: filters.sampler || null,
      filters:
        filters.district && filters.district.length > 0
          ? getSamplerFilters()
          : [],
      filterMode: "menu",
      filterSearch: true,
      ...(filters.district && filters.district.length > 0
        ? {}
        : {
          filterDropdown: () => {
            return (
              <div style={{ padding: 8 }}>Please select district first</div>
            );
          },
        }),
      render: (text, record) => {
        if (userRole === "support_team" && userRole === "csc_admin") {
          return (
            <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
              {record.samplerInfo?.name || "-"}
            </div>
          );
        }
        const isSamplerEnable =
          record.status === "Pending" || record.status === "Booked";
        return (
          <Tooltip
            title={
              isSamplerEnable
                ? ""
                : "Sampler change only allowed for Pending or Booked."
            }
          >
            <Select
              showSearch
              loading={!!samplerUpdating[record.id]}
              disabled={!!samplerUpdating[record.id] || !isSamplerEnable}
              style={{ width: "100%" }}
              placeholder="Select sampler"
              optionFilterProp="children"
              value={record.samplerInfo?.name || ""}
              onChange={(val) => {
                setSelectedSamplerChange({ id: record.orderId, value: val });
                setConfirmationType("sampler");
                setIsConfirmationModalVisible(true);
              }}
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {record.orderData?.samplerList?.map((sampler) => (
                <Select.Option key={sampler._id} value={sampler._id}>
                  {sampler.name}
                </Select.Option>
              ))}
            </Select>
          </Tooltip>
        );
      },
      hidden:
        userRole === "company_admin" ||
        userRole === "regulatory_body_admin" ||
        userRole === "csc_admin",
    },
    {
      title: "Commodity",
      dataIndex: "commodity",
      width: "10%",
      filteredValue: filters.commodity || null,
      filters: getCommodityFilters(),
      filterMode: "menu",
      filterSearch: true,
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text}
        </div>
      ),
    },
    {
      title: "Variant",
      dataIndex: "variant",
      width: "10%",
      filteredValue: filters.variant || null,
      filters: getVariantFilters(),
      filterMode: "menu",
      filterSearch: true,
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text}
        </div>
      ),
    },
    {
      title: "Sample Seal",
      dataIndex: "sampleSeal",
      width: "10%",
      ...getColumnSearchProps("sampleSeal"),
      filteredValue: filters.sampleSeal || null,
      render: (text) => <div style={{ whiteSpace: "nowrap" }}>{text}</div>,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: "12%",
      filteredValue: filters.status || null,
      filters: getStatusFilters(),
      filterMode: "menu",
      filterSearch: true,
      render: (text) => (
        <Tag
          color={statusColors[text] || "#0a8080"}
          key={text}
          style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Sector",
      dataIndex: "sector",
      width: "10%",
      filters: getSectorsFilters(),
      filteredValue: filters.sector || null,
      filterSearch: true,
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text || "-"}
        </div>
      ),
    },
    {
      title: "Tracking Id",
      dataIndex: "trackingId",
      width: "12%",
      render: (text) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {text || "-"}
        </div>
      ),
    },
    {
      width: "5%",
      render: (text, record) => (
        <Space size="middle">
          <Dropdown overlay={getMenuItems(record)}>
            <Tooltip title="Actions">
              <Button type="text" shape="circle" icon={<MoreOutlined />} />
            </Tooltip>
          </Dropdown>
        </Space>
      ),
    },
  ];

  const columns = allColumns.filter((col) => !col.hidden);
  const handleManualRefresh = () => {
    const query = buildOrderListQuery();
    dispatch(actions.fetchOrders({ query }));
    dispatch(actions.fetchPendingOrderReport());
    setLastRefreshTime(Date.now());

    notification.success({
      message: "Data refreshed successfully",
      duration: 2,
    });
  };

  const handleResetFilters = () => {
    setSelectedRow(new Map()); // Reset selected rows
    setSelectedRowKeys([]); // Reset selected row keys

    setCurrentPage(1);
    setPageSize(10);
    setPrevStateFilter(null);
    setPrevDistrictFilter(null);

    // Clear refs
    if (orderIdFilterRef.current) {
      orderIdFilterRef.current.clearFilters();
      orderIdFilterRef.current.confirm({ closeDropdown: true });
    }
    if (dateSearchFilterRef.current) {
      dateSearchFilterRef.current.clearFilters();
      dateSearchFilterRef.current.confirm({ closeDropdown: true });
    }
    dispatch(actions.setFilters({}));
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error("Failed to clear filters:", e);
    }
  };

  const { company, projectCodeId } = filters;
  const handleTablePagination = (pagination, newFilters, sorter) => {
    // Merge with existing filters instead of replacing
    if (!newFilters.hasOwnProperty("company")) {
      newFilters.company = filters.company;
    }
    if (!newFilters.hasOwnProperty("projectCodeId")) {
      newFilters.projectCodeId = filters.projectCodeId;
    }

    setSelectedRow(new Map());
    setSelectedRowKeys([]);

    const newPage = pagination?.current;
    const newPageSize = pagination?.pageSize;

    // Handle state changes
    if (newFilters?.state?.[0] !== prevStateFilter) {
      if (!newFilters?.state || newFilters.state.length === 0) {
        newFilters.district = null;
        newFilters.sampler = null;
        setPrevStateFilter(null);
      } else {
        newFilters.district = null;
        newFilters.sampler = null;
        setPrevStateFilter(newFilters.state[0]);
      }
    }

    // Handle district changes
    if (newFilters?.district?.[0] !== prevDistrictFilter) {
      if (!newFilters?.district || newFilters.district.length === 0) {
        newFilters.sampler = null;
        setPrevDistrictFilter(null);
      } else {
        newFilters.sampler = null;
        setPrevDistrictFilter(newFilters.district[0]);
      }
    }

    // Update Redux state
    dispatch(actions.setFilters({ ...newFilters }));

    if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }

    // Build and update URL (only add non-default values)
    const queryParams = new URLSearchParams();

    if (newPage !== 1) {
      queryParams.append("page", newPage);
    }
    if (newPageSize !== 10) {
      queryParams.append("limit", newPageSize);
    }

    // Add filter params
    if (newFilters.status) {
      newFilters.status.forEach((cat) => queryParams.append("statusId", cat));
    }
    if (newFilters.lab) {
      newFilters.lab.forEach((el) => queryParams.append("labAssignedId", el));
    }
    if (newFilters.logistics) {
      newFilters.logistics.forEach((el) =>
        queryParams.append("assignedLogisticsId", el)
      );
    }
    if (newFilters.userName) {
      newFilters.userName.forEach((el) => queryParams.append("user", el));
    }
    if (newFilters.orderId) {
      queryParams.append("orderId", newFilters.orderId);
    }
    if (newFilters.sampleSeal) {
      queryParams.append("sampleSealNo", newFilters.sampleSeal);
    }
    if (newFilters.commodity) {
      newFilters.commodity.forEach((el) =>
        queryParams.append("commodityId", el)
      );
    }
    if (newFilters.variant) {
      newFilters.variant.forEach((el) =>
        queryParams.append("sampleVariant", el)
      );
    }
    if (newFilters.state) {
      newFilters.state.forEach((el) => queryParams.append("stateId", el));
    }
    if (newFilters.district) {
      newFilters.district.forEach((el) => queryParams.append("districtId", el));
    }
    if (newFilters.sampler && newFilters.sampler.length > 0) {
      newFilters.sampler.forEach((el) => queryParams.append("samplerId", el));
    }
    if (newFilters.createdOn?.length === 2) {
      const startDate = startDateFormatter(newFilters.createdOn[0]);
      const endDate = endDateFormatter(newFilters.createdOn[1]);
      queryParams.append("createdAtFrom", startDate);
      queryParams.append("createdAtTo", endDate);
    }
    if (newFilters.company) {
      queryParams.append("companyId", newFilters.company);
    }
    if (newFilters.projectCodeId) {
      queryParams.append("projectCodeId", newFilters.projectCodeId);
    }
    if (newFilters.sector) {
      newFilters.sector.forEach((el) => queryParams.append("sector", el));
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);

      const newMap = new Map();
      selectedRows.forEach((row) => {
        newMap.set(row.orderId, row.orderData);
      });
      setSelectedRow(newMap);
    },

    getCheckboxProps: (record) => ({
      disabled: null,
      name: record.name,
    }),
  };

  // const selectedClientId = addOrderWatch("client");
  // useEffect(() => {
  //   if (selectedClientId) {
  //     dispatch(
  //       actions.fetchProjectCodes({
  //         companyId: selectedClientId,
  //         for: "AddOrder",
  //       })
  //     );
  //   }
  //   addOrderSetValue("projectCode", "");
  // }, [selectedClientId, dispatch, addOrderSetValue]);
  // Handle progression through wizard steps

  const handleWizardSubmit = (callbacks = {}) => {
    const submitData = {
      commodityId: wizardData.basicInfo.commodity,
      sampleVariant: wizardData.basicInfo.variant,
      sampleType: wizardData.basicInfo.sampleType,
      sector: wizardData.basicInfo.sector,
      stateId: wizardData.basicInfo.state,
      districtId: wizardData.basicInfo.district,
      companyId: wizardData.basicInfo.client,
      projectCodeId: wizardData.basicInfo.projectCode,
      agencyId: wizardData.basicInfo.agency,

      destinationCountry: wizardData.basicInfo.destinationCountry,
      samplingType: wizardData.basicInfo.samplingType,
      expectedConsignmentReadiness: wizardData.basicInfo
        .expectedConsignmentReadiness
        ? new Date(wizardData.basicInfo.expectedConsignmentReadiness)
        : undefined,
      expectedShipmentDate: wizardData.basicInfo.expectedShipmentDate
        ? new Date(wizardData.basicInfo.expectedShipmentDate)
        : undefined,
      packageId: wizardData?.selectedPackage?.id,

      ...(wizardData.basicInfo.sampler && {
        samplerId: wizardData.basicInfo.sampler,
      }),
      ...(wizardData.basicInfo.siteAddress && {
        siteAddress: wizardData.basicInfo.siteAddress,
      }),
      ...(wizardData.basicInfo.codewiseReason && {
        codewiseReason: wizardData.basicInfo.codewiseReason,
      }),

      // ...(wizardData.codeListData && wizardData.codeListData.length > 0 && {
      //   codeEntries: wizardData.codeListData,
      // }),
      ...(wizardData.additionalInfo?.batchId && {
        batchId: wizardData.additionalInfo.batchId,
      }),
      ...(wizardData.additionalInfo?.supplier && {
        supplier: wizardData.additionalInfo.supplier,
      }),
      ...(wizardData.additionalInfo?.quantity && {
        quantity: wizardData.additionalInfo.quantity,
      }),
      ...(wizardData.additionalInfo?.quantityUnit && {
        quantityUnit: wizardData.additionalInfo.quantityUnit,
      }),
      ...(wizardData.additionalInfo?.notes && {
        additionalNotes: wizardData.additionalInfo.notes,
      }),
      ...(wizardData.additionalInfo?.documents && {
        documents: wizardData.additionalInfo.documents?.map((doc) => ({
          _id: doc._id,
          fileName: doc?.fileName || "",
          documentName: doc?.documentName || "",
          document: doc?.document || "",
          documentType: doc?.documentType || "",
        })),
      }),
    };

    dispatch(
      actions.addOrder({
        data: submitData,
        onSuccess: (response) => {
          notification.success({
            message: "Order added successfully!",
            description: `Order ID: ${response.document.orderId}`,
            placement: "bottomLeft",
          });
          if (callbacks.onSuccess) {
            callbacks.onSuccess(response.document.orderId);
          }
        },
        onFailure: (error) => {
          notification.error({
            message: error?.message || "Failure in adding order",
            placement: "bottomLeft",
          });

          if (callbacks.onFailure) {
            callbacks.onFailure();
          }
        },
      })
    );
  };

  const handleWizardClose = () => {
    setOpen(false);
    setWizardStep(0);
    setWizardData({
      basicInfo: {},
      // codeListData: [],
      selectedPackage: null,
      additionalInfo: {},
    });
    dispatch(actions.clearPackages());
  };
  useEffect(() => {
    if (isAutoRefreshEnabled) {
      const interval = setInterval(() => {
        // Only refresh if user is on the page (document is visible)
        if (document.visibilityState === "visible") {
          const query = buildOrderListQuery();
          dispatch(actions.fetchOrders({ query }));
          dispatch(actions.fetchPendingOrderReport());
          setLastRefreshTime(Date.now());
        }
      }, 5 * 60 * 1000); // 5 minutes

      setRefreshInterval(interval);

      return () => {
        if (interval) {
          clearInterval(interval);
        }
      };
    }
  }, [
    isAutoRefreshEnabled,
    filters,
    currentPage,
    pageSize,
    dispatch,
    buildOrderListQuery,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [refreshInterval]);

  const statusIdMap = orderStatus.reduce((acc, item) => {
    acc[item.value] = { label: item.label, id: item._id };
    return acc;
  }, {});
  const ORDER_STATUS_TRANSITIONS = {
    pending: {
      nextStatusValue: "booked",
      nextStatusLabel: statusIdMap["booked"]?.label,
      nextStatusId: statusIdMap["booked"]?.id,
    },
    readyForPickup: {
      nextStatusValue: "outForPickup",
      nextStatusLabel: statusIdMap["outForPickup"]?.label,
      nextStatusId: statusIdMap["outForPickup"]?.id,
    },
    outForPickup: {
      nextStatusValue: "deliveredToLab",
      nextStatusLabel: statusIdMap["deliveredToLab"]?.label,
      nextStatusId: statusIdMap["deliveredToLab"]?.id,
    },
    reportUploaded: {
      nextStatusValue: "completed",
      nextStatusLabel: statusIdMap["completed"]?.label,
      nextStatusId: statusIdMap["completed"]?.id,
    },
  };

  const notifyFailure = (message, failedOrderIds = []) => {
    notification.warning({
      message,
      description: `Failed for orders: ${failedOrderIds
        .map((item) => item.orderId)
        .join(", ")}`,
    });
  };

  const handleBulkLabAssignment = () => {
    const orderIds = [...selectedRow.keys()];

    if (orderIds.length === 0) {
      message.warning("No rows selected for lab assignment.");
      return;
    }

    if (!selectedLab) {
      message.warning("Please first select Lab.");
      return;
    }

    const data = orderIds.map((orderId) => ({
      orderId: orderId,
      labToBeAssignedId: selectedLab,
    }));
    setIsBulkLabLoading(true);
    dispatch(
      actions.bulkLabAssignment({
        data,
        onSuccess: (res) => {
          setIsBulkLabModalVisible(false);
          setIsBulkLabLoading(false);
          fetchOderList();
          setSelectedLab(null);
          setSelectedRow(new Map());
          setSelectedRowKeys([]);
          if (res?.failedOrderIds?.length) {
            notifyFailure(res.message, res.failedOrderIds);
          } else {
            notification.success({
              message: "Lab assigned successfully to selected order!",
            });
          }
        },
        onFailure: () => {
          setIsBulkLabLoading(false);
          notification.error({
            message: "Fail in assigning Labs.",
          });
        },
      })
    );
  };

  const handleBulkChangeStatus = () => {
    const orderIds = [...selectedRow.keys()];

    if (orderIds.length === 0) {
      message.warning("No rows selected for status change.");
      return;
    }

    if (!nextStatusObj?.nextStatusId) {
      message.warning("Please first select a valid status.");
      return;
    }

    const data = orderIds.map((orderId) => ({
      orderId: orderId,
      statusId: nextStatusObj.nextStatusId,
    }));

    setIsBulkChangeStatusLoading(true);
    dispatch(
      actions.bulkChangeOrderStatus({
        data,
        onSuccess: (res) => {
          setIsBulkChangeStatusModalVisible(false);
          setIsBulkChangeStatusLoading(false);
          fetchOderList();
          setSelectedRow(new Map());
          setSelectedRowKeys([]);
          if (res?.failedOrderIds?.length) {
            notifyFailure(res.message, res.failedOrderIds);
          } else {
            notification.success({
              message: " Order status successfully changed to selected order!",
            });
          }
        },
        onFailure: (error) => {
          setIsBulkChangeStatusLoading(false);
          notification.error({
            message: "Failure in Updating Order status.",
            description: error?.message || "Something went wrong.",
          });
        },
      })
    );
  };

  const handleBulkLogisticAssignment = () => {
    const orderIds = [...selectedRow.keys()];

    if (orderIds.length === 0) {
      message.warning("No rows selected for Logistic assignment.");
      return;
    }

    if (!selectedLogistic) {
      message.warning("Please first select Logistic.");
      return;
    }

    const data = orderIds.map((orderId) => ({
      orderId: orderId,
      logisticId: selectedLogistic,
      trackingId: trackingIdInput,
    }));
    setIsBulkLogisticLoading(true);
    dispatch(
      actions.logisticsAssignment({
        data,
        onSuccess: (res) => {
          setIsBulkLogisticModalVisible(false);
          setIsBulkLogisticLoading(false);
          fetchOderList();
          setSelectedLogistic(null);
          setTrackingIdInput("");
          setSelectedRow(new Map());
          setSelectedRowKeys([]);
          if (res?.failedOrderIds?.length) {
            notifyFailure(res.message, res.failedOrderIds);
          } else {
            notification.success({
              message: "Logistic assigned successfully to selected order!",
            });
          }
        },
        onFailure: () => {
          setIsBulkLogisticLoading(false);
          notification.error({
            message: "Failure in assigning Logistic.",
          });
        },
      })
    );
  };

  const handleBulkSamplerAssignment = () => {
    const orderIds = [...selectedRow.keys()];

    if (orderIds.length === 0) {
      message.warning("No rows selected for Sampler assignment.");
      return;
    }

    if (!selectedSampler) {
      message.warning("Please first select sampler.");
      return;
    }

    const data = orderIds.map((orderId) => ({
      orderId: orderId,
      samplerId: selectedSampler,
    }));
    setIsBulkSamplerLoading(true);
    dispatch(
      actions.bulkSamplerAssignment({
        data,
        onSuccess: (res) => {
          setIsBulkSamplerModalVisible(false);
          setIsBulkSamplerLoading(false);
          setSelectedSampler(null);
          setSelectedRow(new Map());
          setSelectedRowKeys([]);
          fetchOderList();
          if (res?.failedOrderIds?.length) {
            notifyFailure(res.message, res.failedOrderIds);
          } else {
            notification.success({
              message: "Sampler assigned successfully to selected order!",
            });
          }
        },
        onFailure: () => {
          setIsBulkSamplerLoading(false);
          notification.error({
            message: "Failure in assigning Sampler.",
          });
        },
      })
    );
  };

  const handleAddTrackingId = (fieldData) => {
    const data = [
      {
        orderId: orderDetails?.orderId,
        trackingId: fieldData?.trackingId,
      },
    ];
    setIsTrackingLoading(true);
    dispatch(
      actions.logisticsAssignment({
        data,
        onSuccess: (res) => {
          setIsTrackingModalVisible(false);
          setIsTrackingLoading(false);
          fetchOderList();
          setOrderDetails(null);
          notification.success({
            message: "Tracking ID added successfully!",
          });
        },
        onFailure: () => {
          setIsTrackingLoading(false);
          notification.error({
            message: "Failure in adding Tracking ID.",
          });
        },
      })
    );
  };

  let currentStatusObj;
  let nextStatusObj;

  if (selectedRow && selectedRow.size > 0) {
    const firstRow = [...selectedRow.values()][0];
    currentStatusObj = firstRow?.orderStatus || {};
    nextStatusObj = ORDER_STATUS_TRANSITIONS[currentStatusObj?.value] || {};
  }
  const { disabled: isBulkLabDisabled, tooltip: bulkLabTooltip } =
    getBulkLabStatus(selectedRow);
  const {
    disabled: isBulkChangeStatusDisabled,
    tooltip: bulkChangeStatusTooltip,
  } = getBulkStatusChangeValidation(
    selectedRow,
    nextStatusObj?.nextStatusValue
  );
  const { disabled: isBulkLogisticDisabled, tooltip: bulkLogisticTooltip } =
    getBulkLogisticsStatus(selectedRow);
  const { disabled: isBulkSamplerDisabled, tooltip: bulkSamplerTooltip } =
    getBulkSamplerAssignStatus(selectedRow);

  const actionMenu = (
    <Menu>
      <Tooltip title={bulkSamplerTooltip}>
        <span>
          <Menu.Item
            key="bulkSamplerAssignment"
            onClick={() => setIsBulkSamplerModalVisible(true)}
            disabled={isBulkSamplerDisabled}
          >
            Sampler Assignment
          </Menu.Item>
        </span>
      </Tooltip>
      <Tooltip title={bulkChangeStatusTooltip}>
        <span>
          <Menu.Item
            key="bulkStatusChange"
            onClick={() => setIsBulkChangeStatusModalVisible(true)}
            disabled={isBulkChangeStatusDisabled}
          >
            Change Status
            {nextStatusObj?.nextStatusLabel && (
              <>
                {" "}
                from{" "}
                <span style={{ color: "#1890ff", fontWeight: "500" }}>
                  {currentStatusObj?.label}
                </span>{" "}
                to{" "}
                <span style={{ color: "#52c41a", fontWeight: "500" }}>
                  {nextStatusObj.nextStatusLabel}
                </span>
              </>
            )}
          </Menu.Item>
        </span>
      </Tooltip>
      <Tooltip title={bulkLabTooltip}>
        <span>
          <Menu.Item
            key="bulkLabAssignment"
            onClick={() => setIsBulkLabModalVisible(true)}
            disabled={isBulkLabDisabled}
          >
            Lab Assignment
          </Menu.Item>
        </span>
      </Tooltip>
      <Tooltip title={bulkLogisticTooltip}>
        <span>
          <Menu.Item
            key="bulkLogisticAssignment"
            onClick={() => setIsBulkLogisticModalVisible(true)}
            disabled={isBulkLogisticDisabled}
          >
            Logistic Assignment
          </Menu.Item>
        </span>
      </Tooltip>
    </Menu>
  );

  const handleClientFilterChange = (val) => {
    if (val) {
      // Fetch project codes for the selected company
      dispatch(actions.fetchProjectCodes({ companyId: val, for: "filter" }));
    } else {
      dispatch(actions.clearProjectCodesForFilter());
    }
    const newFilters = { ...filters, company: val, projectCodeId: null };
    dispatch(actions.setFilters(newFilters));
  };

  const handleProjectFilterChange = (val) => {
    // Update state and URL
    const newFilters = { ...filters, projectCodeId: val };
    dispatch(actions.setFilters(newFilters));
  };

  const additionalFilters = [
    userRole !== "company_admin" &&
      userRole !== "regulatory_body_admin" &&
      userRole !== "csc_admin" ? (
      <Select
        key="client-filter"
        showSearch
        placeholder="Select Client"
        value={filters.company || ""}
        onChange={handleClientFilterChange}
        filterOption={(input, option) =>
          option?.children?.toLowerCase().includes(input.toLowerCase())
        }
        suffixIcon={
          <FilterFilled
            style={{ color: filters.company ? "#0a8080" : undefined }}
          />
        }
        style={{
          width: "170px",
          fontWeight: 600,
        }}
      >
        <Option key="all" value="">
          All Clients
        </Option>
        {[...companies]
          .sort((a, b) => {
            const nameA = a.companyName || "";
            const nameB = b.companyName || "";
            return nameA.localeCompare(nameB);
          })
          .map((company) => (
            <Option key={company._id} value={company._id}>
              {company.companyName}
            </Option>
          ))}
      </Select>
    ) : null,
    <Select
      key="project-filter"
      showSearch
      placeholder="Select Project"
      value={filters.projectCodeId || ""}
      onChange={handleProjectFilterChange}
      filterOption={(input, option) =>
        option?.children?.toLowerCase().includes(input.toLowerCase())
      }
      suffixIcon={
        <FilterFilled
          style={{ color: filters.projectCodeId ? "#0a8080" : undefined }}
        />
      }
      style={{
        width: "170px",
        fontWeight: 600,
        marginLeft: "10px",
      }}
    >
      <Option key="all" value="">
        All Projects
      </Option>
      {[...projectCodesForFilter]
        .sort((a, b) => {
          const codeA = a.projectCode || "";
          const codeB = b.projectCode || "";
          return codeA.localeCompare(codeB);
        })
        .map((project) => (
          <Option key={project._id} value={project._id}>
            {project.projectCode}
          </Option>
        ))}
    </Select>,
  ];

  let headerButtons = [
    ...additionalFilters,
    <Button
      key="refresh"
      type="secondary"
      onClick={handleManualRefresh}
      loading={loading}
      disabled={loading}
      icon={<ReloadOutlined />}
    >
      Refresh
    </Button>,
    <Button key="reset" onClick={handleResetFilters} disabled={loading}>
      Reset Filters
    </Button>,
  ];

  if (
    userRole !== "agnext_reviewer" &&
    userRole !== "regulatory_body_admin" &&
    userRole !== "support_team" &&
    userRole !== "csc_admin"
  ) {
    headerButtons = [
      ...additionalFilters,
      userRole !== "company_admin" && (
        <Dropdown overlay={actionMenu} trigger={["click"]}>
          <Button style={{ width: 150, textAlign: "left", fontWeight: 600 }}>
            {"All Bulk Action"} <DownOutlined />
          </Button>
        </Dropdown>
      ),
      <Button key="add" type="primary" onClick={() => setOpen(true)}>
        Add New Order
      </Button>,

      <Button
        key="refresh"
        type="secondary"
        onClick={handleManualRefresh}
        loading={loading}
        disabled={loading}
        icon={<ReloadOutlined />}
      >
        Refresh
      </Button>,
      <Button key="reset" onClick={handleResetFilters} disabled={loading}>
        Reset Filters
      </Button>,
    ];
  }

  return (
    <div style={{ height: "100%" }}>
      <PageHeader ghost={false} title="Orders" extra={headerButtons} />
      <AddOrderWizard
        visible={open}
        onClose={handleWizardClose}                                         
        userRole={userRole}
        commodities={commodities}
        states={states}
        districts={districts}
        samplers={samplers}
        sectors={sectors}
        projectCodes={projectCodesForAddOrder}
        companies={companies}
        packages={packages}
        fetchingPackages={fetchingPackages}
        packagesError={packagesError}
        wizardData={wizardData}
        currentStep={wizardStep}
        onStepChange={handleStepChange}
        onNext={goNext}
        onBack={goBack}
        onSubmit={handleWizardSubmit}
        onFieldChange={handleFieldChange}
        setWizardData={setWizardData}
      />{" "}
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          overflowX: "auto",
          padding: 24,
        }}
      >
        {userRole === "company_admin" ||
          userRole === "agnext_reviewer" ||
          userRole === "regulatory_body_admin" ||
          userRole === "csc_admin" ? (
          <Table
            rowKey="orderId"
            dataSource={orderData}
            columns={columns}
            loading={orderPageLoading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalOrders,
              showSizeChanger: true,
              disabled: orderData.length === 0 ? true : false,
            }}
            scroll={{ x: 2300 }}
            onChange={handleTablePagination}
          />
        ) : (
          <Table
            rowKey="orderId"
            dataSource={orderData}
            columns={columns}
            loading={orderPageLoading}
            rowSelection={rowSelection}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalOrders,
              showSizeChanger: true,
              disabled: orderData.length === 0 ? true : false,
            }}
            scroll={{ x: 2300 }}
            onChange={handleTablePagination}
          />
        )}
      </div>
      {/* <Modal
        title="Add Order"
        visible={addOrderModalVisible}
        onCancel={() => {
          setAddOrderModalVisible(false);
          resetAddOrderForm();
        }}
        footer={[
          <Button
            key="back"
            onClick={() => {
              setAddOrderModalVisible(false);
              resetAddOrderForm();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={addOrderHandleSubmit(onAddOrderSubmit)}
          >
            Submit
          </Button>,
        ]}
      >
        <AddOrderForm
          control={addOrderControl}
          errors={addOrderErrors}
          watch={addOrderWatch}
          options={{
            commodities: commodities?.map((commodity) => ({
              label: commodity.name,
              value: commodity._id,
            })),
            states: states?.map((state) => ({
              label: state.name,
              value: state._id,
            })),
            districts: districts?.map((district) => ({
              label: district.city,
              value: district._id,
            })),
            samplers: samplers?.map((sampler) => ({
              label: sampler.name,
              value: sampler._id,
            })),
            projectCodes: projectCodesForAddOrder?.map((projectCode) => ({
              label: projectCode.projectCode,
              value: projectCode._id,
            })),
            companies: getCompanyFilters().map((company) => ({
              label: company.text,
              value: company.value,
            })),
            sectors: sectors,
          }}
        />
      </Modal> */}
      <Modal
        title="Upload Report"
        visible={reportModalVisible}
        onOk={handleUploadReport}
        okButtonProps={{
          disabled: !reportFile ? true : false,
        }}
        okText="Upload"
        onCancel={handleCancelReportUpload}
      >
        <FileUpload
          placeholder="Select .zip file with Report(s)"
          allowZip={true}
          onChange={handleFileChange}
          name="report"
          files={reportFile}
          handleRemove={handleRemoveFile}
        />
        {reportError && (
          <p
            style={{
              color: "red",
              fontSize: "14px",
              marginLeft: "8px",
            }}
          >
            Please upload .pdf or .zip files only
          </p>
        )}
        {reportFileSizeError && (
          <p
            style={{
              color: "red",
              fontSize: "14px",
              marginLeft: "8px",
            }}
          >
            Size of the file exceeds 12 MB. Please try to upload file size less
            than 12 MB
          </p>
        )}
        <p
          style={{
            marginTop: "10px",
            color: "#75797d",
            fontSize: "12px",
            fontStyle: "italic",
          }}
        >
          * Uploaded file size should not exceed more than 12 MB
        </p>
      </Modal>
      <Modal
        title="Upload Purchase Order"
        visible={poModalVisible}
        onOk={handleUploadPurchaseOrder}
        okButtonProps={{
          disabled: !purchaseOrderNum || !poFile ? true : false,
        }}
        okText="Upload"
        onCancel={handleCancelPurchaseOrderUpload}
      >
        <FileUpload
          placeholder="Select Purchase Order file"
          onChange={handlePOFileChange}
          name="purchaseOrder"
          files={poFile}
          handleRemove={handleRemovePOFile}
        />
        {purchaseOrderFileError && (
          <p
            style={{
              color: "red",
              fontSize: "14px",
              marginLeft: "22px",
            }}
          >
            Please upload .pdf files only
          </p>
        )}
        {purchaseOrderFileSizeError && (
          <p
            style={{
              color: "red",
              fontSize: "14px",
              marginLeft: "22px",
            }}
          >
            Size of the file exceeds 12 MB. Please try to upload file size less
            than 12 MB
          </p>
        )}
        <Input
          onFocus={() => {
            setPurchaseOrderNumberError(null);
          }}
          onChange={(e) => setPurchaseOrderNum(e.target.value)}
          value={purchaseOrderNum}
          placeholder="Purchase Order Number"
        />

        {purchaseOrderNumberError && (
          <p
            style={{
              color: "red",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            {purchaseOrderNumberError}
          </p>
        )}
        <p
          style={{
            marginTop: "10px",
            color: "#75797d",
            fontSize: "12px",
            fontStyle: "italic",
          }}
        >
          * Uploaded file size should not exceed more than 12 MB
        </p>
      </Modal>
      <Modal
        title="Order Instructions"
        visible={instructionsViewModal}
        footer={null}
        onCancel={() => setInstructionsViewModal(false)}
      >
        <p>{instruction}</p>
      </Modal>
      <Modal
        title="Tracking Details"
        visible={trackingViewModal}
        footer={null}
        onCancel={() => setTrackingViewModal(false)}
      >
        <p>{trackingDetails}</p>
      </Modal>
      <AddInstructionsForm
        visible={instrunctionModalVisible}
        setVisible={setInstructionModalVisible}
        onSave={handleOrderInstructions}
      />
      <AddTrackingDetailsForm
        visible={isTrackingModalVisible}
        setVisible={setIsTrackingModalVisible}
        onSave={handleAddTrackingId}
        isLoading={isTrackingLoading}
        setOrderDetails={setOrderDetails}
        orderDetails={orderDetails}
      />
      {/* Bulk lab assigment  */}
      <Modal
        title="Bulk Lab Assignment"
        visible={isBulkLabModalVisible}
        onCancel={() => {
          setIsBulkLabModalVisible(false);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsBulkLabModalVisible(false);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="bulkLabAssign"
            type="primary"
            loading={isBulkLabLoading}
            onClick={handleBulkLabAssignment}
          // disabled={!selectedLab}
          >
            Confirm Bulk Lab Assignment
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>
            {selectedRow?.size} order{selectedRow?.size > 1 ? "s" : ""} selected
          </Text>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Text strong>Select Lab:</Text>
          <Select
            showSearch
            placeholder="Select lab"
            value={selectedLab || "All Labs"}
            onChange={(val) => setSelectedLab(val)}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            style={{ width: "100%", marginTop: 8 }}
            size="large"
          >
            {getLabOptions()}
          </Select>
        </div>
        <Text type="secondary">
          This action will assign the selected lab to all chosen orders.
        </Text>
      </Modal>
      {/* Bulk Change Status  */}
      <Modal
        title="Bulk Change Status"
        visible={isBulkChangeStatusModalVisible}
        onCancel={() => {
          setIsBulkChangeStatusModalVisible(false);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsBulkChangeStatusModalVisible(false);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="bulkChangeStatus"
            type="primary"
            loading={isBulkChangeStatusLoading}
            onClick={handleBulkChangeStatus}
          // disabled={isBulkChangeStatusDisabled}
          >
            Confirm Bulk Status Change
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>
            {selectedRow?.size} order{selectedRow?.size > 1 ? "s" : ""} selected
          </Text>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Text>Change status from </Text>
          <Text style={{ color: "#1890ff", fontWeight: "500" }}>
            {currentStatusObj?.label || "N/A"}
          </Text>
          <Text> to </Text>
          <Text style={{ color: "#52c41a", fontWeight: "500" }}>
            {nextStatusObj?.nextStatusLabel || "N/A"}
          </Text>
        </div>
        <Text type="secondary">
          This action will update the status for all selected orders.
        </Text>
      </Modal>
      {/* Bulk logistic assigment  */}
      <Modal
        title="Bulk Logistic Assignment"
        visible={isBulkLogisticModalVisible}
        onCancel={() => {
          setIsBulkLogisticModalVisible(false);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsBulkLogisticModalVisible(false);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="bulkLogisticAssignment"
            type="primary"
            loading={isBulkLogisticLoading}
            onClick={handleBulkLogisticAssignment}
          // disabled={!selectedLab}
          >
            Confirm Bulk Logistic Assignment
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>
            {selectedRow?.size} order{selectedRow?.size > 1 ? "s" : ""} selected
          </Text>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Text strong>Select Logistic:</Text>
          <Select
            showSearch
            placeholder="Select Logistic"
            value={selectedLogistic || "All Logistic"}
            onChange={(val) => setSelectedLogistic(val)}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            style={{ width: "100%", marginTop: 8 }}
            size="large"
          >
            {getLogisticsOptions()}
          </Select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Text strong>Tracking ID:</Text>
          <Input
            placeholder="Enter Tracking ID"
            value={trackingIdInput}
            onChange={(e) => setTrackingIdInput(e.target.value)}
            style={{ width: "100%", marginTop: 8 }}
            size="large"
          />
        </div>
        <Text type="secondary">
          This action will assign the selected logistic and tracking id to all
          chosen orders.
        </Text>
      </Modal>
      {/* Bulk Sampler assigment  */}
      <Modal
        title="Bulk Sampler Assignment"
        visible={isBulkSamplerModalVisible}
        onCancel={() => {
          setIsBulkSamplerModalVisible(false);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsBulkSamplerModalVisible(false);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="bulkLabAssign"
            type="primary"
            loading={isBulkSamplerLoading}
            onClick={handleBulkSamplerAssignment}
          // disabled={!selectedSampler}
          >
            Confirm Bulk Sampler Assignment
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>
            {selectedRow?.size} order{selectedRow?.size > 1 ? "s" : ""} selected
          </Text>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Text strong>Select Sampler:</Text>
          <Select
            showSearch
            placeholder="Select Sampler"
            value={selectedSampler || "All Samplers"}
            onChange={(val) => setSelectedSampler(val)}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            style={{ width: "100%", marginTop: 8 }}
            size="large"
          >
            {([...selectedRow.values()][0]?.samplerList || []).map(
              (sampler) => (
                <Select.Option key={sampler._id} value={sampler._id}>
                  {sampler.name}
                </Select.Option>
              )
            )}
          </Select>
        </div>
        <Text type="secondary">
          This action will assign the selected sampler to all chosen orders.
        </Text>
      </Modal>
      <ConfirmationModal
        visible={isConfirmationModalVisible}
        loading={isConfirmLoading}
        message={
          confirmationType === "lab"
            ? "Lab?"
            : confirmationType === "logistics"
              ? "Logistic Partner?"
              : "Sampler?"
        }
        onCancel={() => {
          setIsConfirmationModalVisible(false);
          setSelectedLogisticPartner(null);
          setSelectedLabChange(null);
          setSelectedSamplerChange(null);
          setConfirmationType(null);
        }}
        onConfirm={() => {
          setIsConfirmLoading(true);

          if (confirmationType === "logistics" && selectedLogisticPartner) {
            handleChangeLogisticPartner({
              id: selectedLogisticPartner.id,
              value: selectedLogisticPartner.value,
            });
          } else if (confirmationType === "lab" && selectedLabChange) {
            handleChangeLab({
              id: selectedLabChange.id,
              value: selectedLabChange.value,
            });
          } else if (confirmationType === "sampler" && selectedSamplerChange) {
            handleChangeSampler({
              id: selectedSamplerChange.id,
              value: selectedSamplerChange.value,
            });
          }

          setSelectedLogisticPartner(null);
          setSelectedLabChange(null);
          setSelectedSamplerChange(null);
        }}
      />
    </div>
  );
};

export default OrderListing;
